/**
 * ============================================================================
 * KbLinkHoverCard — Popover contextual estilo GitBook exibido quando o
 *                   cursor está DENTRO de uma mark de link (externo ou
 *                   wiki-link entre artigos).
 *
 * Mostra:
 *   - Rótulo do destino (URL/domínio para externo, título do artigo para
 *     wiki-link, ou "Link quebrado" para wiki-link órfão).
 *   - Botões: Editar (abre KbLinkEditorDialog), Copiar URL, Remover link.
 *
 * Coexiste com o `KbTextBubbleMenu`: este último NÃO aparece quando alguma
 * mark de link está ativa (deixa este bubble assumir).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.2 - updateDelay: 0 e shouldShow lê tudo de
 *            `state` (não `editor.isActive`) para evitar flicker/dessincronia
 *            entre os dois BubbleMenus.

 *            seleção está vazia OU inteiramente contida no range da mark de
 *            link) e offset maior para não colidir com o KbTextBubbleMenu.
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */

import { BubbleMenu } from "@tiptap/react/menus";
import type { Editor } from "@tiptap/react";
import { Pencil, X, Copy, ExternalLink, Link as LinkIcon, FileText, AlertTriangle } from "lucide-react";
import { useKbPageLinkResolver } from "@/hooks/kb/useKbPageLinkResolver";
import { openLinkEditor, type LinkEditorInitial, type LinkEditorResult } from "@/lib/kb/pageLinkPicker";
import { toast } from "sonner";

interface Props { editor: Editor | null }

/** Retorna o initial adequado ao ponto do cursor (external ou page). */
function buildInitialFromEditor(editor: Editor): LinkEditorInitial | null {
  const pageAttrs = editor.getAttributes("kbPageLink");
  if (pageAttrs && pageAttrs.pageId) {
    return {
      kind: "page",
      pageId: String(pageAttrs.pageId),
      spaceId: pageAttrs.spaceId ? String(pageAttrs.spaceId) : null,
      anchor: pageAttrs.anchor ? String(pageAttrs.anchor) : null,
      snapshotTitle: pageAttrs.snapshotTitle ? String(pageAttrs.snapshotTitle) : null,
    };
  }
  const linkAttrs = editor.getAttributes("link");
  if (linkAttrs && linkAttrs.href) {
    return { kind: "external", href: String(linkAttrs.href) };
  }
  return null;
}

/** Aplica o resultado do editor de link ao editor Tiptap. */
function applyLinkResult(editor: Editor, result: LinkEditorResult, initialKind: "external" | "page") {
  const chain = editor.chain().focus();
  if (result.kind === "remove") {
    if (initialKind === "external") chain.extendMarkRange("link").unsetLink().run();
    else chain.extendMarkRange("kbPageLink").unsetMark("kbPageLink").run();
    return;
  }
  if (result.kind === "external") {
    // Se estava em wiki-link, remove essa mark antes
    if (initialKind === "page") {
      chain.extendMarkRange("kbPageLink").unsetMark("kbPageLink");
    }
    chain
      .extendMarkRange("link")
      .setLink({ href: result.href, target: "_blank", rel: "noopener noreferrer" } as any)
      .run();
    return;
  }
  if (result.kind === "page") {
    // Se estava em link externo, remove antes
    if (initialKind === "external") {
      chain.extendMarkRange("link").unsetLink();
    }
    chain
      .extendMarkRange("kbPageLink")
      .setMark("kbPageLink", {
        pageId: result.pageId,
        spaceId: result.spaceId,
        anchor: result.anchor ?? null,
        snapshotTitle: result.title,
      })
      .run();
  }
}

export function KbLinkHoverCard({ editor }: Props) {
  if (!editor) return null;

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="kb-link-hover"
      updateDelay={0}
      shouldShow={({ state }) => {
        // Detecção 100% via `state` (não `editor.isActive`) para evitar
        // dessincronia entre a transação corrente e o estado do editor.
        const { selection, schema, doc } = state;
        const linkType = schema.marks.link ?? null;
        const pageType = schema.marks.kbPageLink ?? null;
        if (!linkType && !pageType) return false;

        const { from, to, empty } = selection;

        // Retorna a mark de link no ponto (link externo tem prioridade sobre wiki-link).
        const markAt = (pos: number) => {
          const marks = doc.resolve(pos).marks();
          if (linkType && marks.some((m) => m.type === linkType)) return linkType;
          if (pageType && marks.some((m) => m.type === pageType)) return pageType;
          return null;
        };

        if (empty) {
          // Cursor sem seleção: mostra se estiver dentro de uma mark de link.
          return markAt(Math.max(0, from - 1)) !== null || markAt(from) !== null;
        }

        // Seleção não-vazia: só mostra se a MESMA mark de link cobre ambos
        // os extremos da seleção. Assim uma seleção que sai do link não pisca
        // o hover-card colado ao KbTextBubbleMenu.
        const startMark = markAt(from);
        const endMark = markAt(Math.max(from, to - 1));
        return startMark !== null && startMark === endMark;
      }}
      options={{ placement: "bottom", offset: 12 }}
    >
      <HoverContent editor={editor} />
    </BubbleMenu>
  );
}



// ---------------------------------------------------------------------------
// Conteúdo do bubble
// ---------------------------------------------------------------------------
function HoverContent({ editor }: { editor: Editor }) {
  const isPageLink = editor.isActive("kbPageLink");
  const pageAttrs = editor.getAttributes("kbPageLink");
  const linkAttrs = editor.getAttributes("link");

  // Resolve título ao vivo do wiki-link (usa cache já compartilhado com o hydrator)
  const pageId = isPageLink && pageAttrs?.pageId ? String(pageAttrs.pageId) : null;
  const { data: resolvedMap } = useKbPageLinkResolver(pageId ? [pageId] : []);
  const resolved = pageId ? resolvedMap?.get(pageId) : null;

  // Rótulo/URL a exibir
  let label = "Link";
  let sub = "";
  let broken = false;
  if (isPageLink) {
    if (resolved) {
      if (!resolved.title) { broken = true; label = "Link quebrado"; sub = "artigo removido ou sem acesso"; }
      else {
        label = resolved.title;
        sub = [resolved.space_name, resolved.collection_name].filter(Boolean).join(" › ");
        if (pageAttrs?.anchor) sub = `${sub}${sub ? " · " : ""}#${pageAttrs.anchor}`;
      }
    } else {
      label = pageAttrs?.snapshotTitle ? String(pageAttrs.snapshotTitle) : "Artigo";
    }
  } else if (linkAttrs?.href) {
    label = String(linkAttrs.href);
    try {
      const u = new URL(label);
      sub = u.hostname;
    } catch { /* href relativo */ }
  }

  const initialKind: "external" | "page" = isPageLink ? "page" : "external";

  const handleEdit = () => {
    const initial = buildInitialFromEditor(editor);
    if (!initial) return;
    openLinkEditor(initial, (result) => applyLinkResult(editor, result, initialKind));
  };

  const handleCopy = async () => {
    let toCopy = "";
    if (isPageLink) {
      // Copia o hash-URL de fallback (o hydrator troca por rota real quando montado)
      toCopy = `#kb-page:${pageAttrs?.pageId}${pageAttrs?.anchor ? `#${pageAttrs.anchor}` : ""}`;
    } else if (linkAttrs?.href) {
      toCopy = String(linkAttrs.href);
    }
    if (!toCopy) return;
    try {
      await navigator.clipboard.writeText(toCopy);
      toast.success("URL copiada");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const handleRemove = () => {
    if (isPageLink) {
      editor.chain().focus().extendMarkRange("kbPageLink").unsetMark("kbPageLink").run();
    } else {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }
  };

  return (
    <div
      data-kb-editor-ui="true"
      // Impede que clicar no bubble tire a seleção do ProseMirror (perderia o alvo do comando).
      onMouseDown={(e) => e.preventDefault()}
      className="flex items-center gap-1 rounded-md border bg-popover text-popover-foreground shadow-md pl-2 pr-1 py-1 max-w-[420px]"
    >
      <div className="flex items-center gap-1.5 min-w-0 pr-1">
        {broken ? (
          <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
        ) : isPageLink ? (
          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <div className="min-w-0">
          <div className={`text-xs font-medium truncate ${broken ? "text-destructive" : ""}`}>
            {label}
          </div>
          {sub && (
            <div className="text-[10px] text-muted-foreground truncate">{sub}</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-0.5 border-l pl-1">
        <IconBtn title="Editar link" onClick={handleEdit}>
          <Pencil className="h-3.5 w-3.5" />
        </IconBtn>
        <IconBtn title="Copiar URL" onClick={handleCopy}>
          <Copy className="h-3.5 w-3.5" />
        </IconBtn>
        <IconBtn title="Remover link" onClick={handleRemove} destructive>
          <X className="h-3.5 w-3.5" />
        </IconBtn>
      </div>
    </div>
  );
}

function IconBtn({
  onClick, title, children, destructive,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex items-center justify-center h-7 w-7 rounded transition-colors ${
        destructive
          ? "hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          : "hover:bg-accent hover:text-accent-foreground text-muted-foreground"
      }`}
    >
      {children}
    </button>
  );
}
